import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, Calendar, DollarSign, ChevronRight } from 'lucide-react';
import { PaymentReminderService, PaymentReminder } from '../services/PaymentReminderService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PaymentReminderWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<PaymentReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUnpaid: 0,
    dueSoon: 0,
    overdue: 0,
    critical: 0,
    totalUnpaidAmount: 0,
  });

  useEffect(() => {
    if (currentUser?.email) {
      fetchUserReminders();
      fetchPaymentStats();
    }
  }, [currentUser]);

  const fetchUserReminders = async () => {
    if (!currentUser?.email) return;
    
    try {
      const userReminders = await PaymentReminderService.getUserReminders(currentUser.email);
      setReminders(userReminders);
    } catch (error) {
      console.error('Error fetching user reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const paymentStats = await PaymentReminderService.getPaymentStats();
      setStats(paymentStats);
    } catch (error) {
      console.error('Error fetching payment stats:', error);
    }
  };

  const getReminderColor = (type: 'due_soon' | 'overdue' | 'critical') => {
    switch (type) {
      case 'due_soon':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'overdue':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getReminderIcon = (type: 'due_soon' | 'overdue' | 'critical') => {
    switch (type) {
      case 'due_soon':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Calendar className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatDaysOverdue = (days: number) => {
    if (days <= 0) return 'Due soon';
    return `${days} day${days > 1 ? 's' : ''} overdue`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            Payment Reminders
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Track your upcoming and overdue payments
          </p>
        </div>
        {reminders.length > 0 && (
          <button
            onClick={() => navigate('/notifications')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Payment Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{stats.totalUnpaid}</div>
          <div className="text-xs text-gray-500">Unpaid</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.dueSoon}</div>
          <div className="text-xs text-gray-500">Due Soon</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.overdue}</div>
          <div className="text-xs text-gray-500">Overdue</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
          <div className="text-xs text-gray-500">Critical</div>
        </div>
      </div>

      {/* Total Amount */}
      {stats.totalUnpaidAmount > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Total Unpaid Amount</span>
            </div>
            <span className="text-lg font-bold text-gray-900">
              EC${stats.totalUnpaidAmount.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">✅</div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">All caught up!</h3>
          <p className="text-sm text-gray-500">
            You don't have any payment reminders at the moment.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reminders.slice(0, 3).map((reminder) => (
            <div
              key={reminder.id}
              className={`p-4 rounded-lg border ${getReminderColor(reminder.reminderType)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {getReminderIcon(reminder.reminderType)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm">
                        Ticket {reminder.ticketCode}
                      </h4>
                      <span className="text-xs font-medium">
                        {formatDaysOverdue(reminder.daysOverdue)}
                      </span>
                    </div>
                    <p className="text-xs mb-2 opacity-90">
                      {reminder.reminderType === 'due_soon' && 'Payment due soon'}
                      {reminder.reminderType === 'overdue' && 'Payment overdue'}
                      {reminder.reminderType === 'critical' && 'Urgent: Payment required'}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">
                        EC${reminder.fineAmount.toLocaleString()}
                      </span>
                      <span className="text-xs opacity-75">
                        Due: {reminder.dueDate.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => navigate(`/payment/${reminder.ticketId}`)}
                  className="flex-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-xs font-medium py-2 px-3 rounded transition-colors"
                >
                  Pay Now
                </button>
                <button
                  onClick={() => navigate(`/tickets/${reminder.ticketId}`)}
                  className="flex-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-xs font-medium py-2 px-3 rounded transition-colors"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
          
          {reminders.length > 3 && (
            <button
              onClick={() => navigate('/notifications')}
              className="w-full text-center text-blue-600 hover:text-blue-700 text-sm font-medium py-2"
            >
              View {reminders.length - 3} more reminder{reminders.length - 3 > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentReminderWidget;
